<template>
  <b-row>
    <b-col>
      <card>

        <template #header>
          <div class="d-flex align-items-center justify-content-between">
            <h1 class="card-title">Voicemails</h1>

            <div v-if="!routeVoicemailId">
              <b-form-group>
                <b-form-radio-group v-model="selectedView" :options="selectViewOptions" button-variant="primary"
                  buttons />
              </b-form-group>
            </div>
          </div>
        </template>

        <div class="mb-2">
          <template v-if="selectedView === 'Incomplete'">

            <b-row v-if="pharmacist" align-v="center">
              <b-col>
                Pharmacist on call: {{ pharmacist }}
              </b-col>
            </b-row>
            <b-row v-if="!routeVoicemailId">
              <b-col>
                <b-form-group label="Team">
                  <v-select
                    v-model="selectedTeam"
                    :options="filterTeamOptions"
                    :reduce="t => t.id"
                    label="name"
                    placeholder="Select Team"
                    :clearable="false"
                  />
                </b-form-group>
              </b-col>
            </b-row>

          </template>
          <template v-else>

            <div class="mb-3">
              <DateRangeFilter v-model="createdDateRangeFilter" label="Received Date:" />
            </div>

          </template>
        </div>

        <div v-for="item in pagedVoicemails" :key="`${item.id}`">
          <card plain @click.native="expandIfClosed(item)" style="cursor:pointer">
            <h6>{{ item.id }}: Received {{ relativeTime(item) }}<span v-if="item.team"> - {{ item.team }}</span></h6>
            <b-collapse :id="`accordion-${item.id}`" accordion="my-accordion" role="tabpanel"
              :ref="`accordion-${item.id}`" style="cursor:auto" @click.native.stop>
              <b-row>
                <b-col>
                  <b-row v-show="item?.phoneNumber?.length ?? 0 > 0">
                    <b-col>
                      <p><b>Phone Number:</b> {{ item.phoneNumber | formatPhone }}</p>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <h6 style="font-weight: 500">Transcription</h6>
                    </b-col>
                  </b-row>
                  <b-row v-if="downloadErrors[item.id]">
                    <b-col>
                      <b-alert variant="danger" show dismissible
                        @dismissed="delete downloadErrors[item.id]" class="py-2 mb-2">
                        Failed to download audio.
                        <a href="#" @click.prevent="downloadVoicemail(item.id)" class="alert-link">Retry</a>
                      </b-alert>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <audio :id="`audio-${item.id}`" controls hidden>
                        <source :id="`source-${item.id}`" type="audio/wav" />
                      </audio>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <transcription-viewer v-if="selectedVoicemail == item.id" entityType="1" :entityId="item.id"
                        :currentTime="getCurrentTimeForVoicemail(item.id)"
                        :compact="transcriptCompactMode"
                        @transcriptclick="transcriptClicked" />
                    </b-col>
                  </b-row>
                </b-col>

                <b-col>
                  <b-form-group label="Triage To">
                    <v-select
                      v-model="triageLocation"
                      :options="allTeams"
                      :reduce="t => t.id"
                      label="name"
                      placeholder="Select Team"
                    />
                  </b-form-group>
                  <b-button class="float-right" variant="info" @click="triageVoicemail(item)">Triage</b-button>
                  <div style="clear: both;"></div>
                  <hr />
                  <br />

                  <SearchComponent :id="`patient-search-${item.id}`" v-model="patients[item.id]" label="Patient"
                    search-u-r-l="/Patient" :initial-search-value="item.phoneNumber" prepopulate autoselect clearable />

                  <router-link v-if="patients[item.id]?.id"
                    :to="{ name: 'PatientDashboard', params: { id: patients[item.id].id } }" class="ml-2">
                    <span>Go To Profile</span> <i class="fas fa-user" />
                  </router-link>

                  <b-button class="float-right" variant="success" @click="completeVoicemail(item)">
                    Complete
                  </b-button>
                </b-col>
              </b-row>
            </b-collapse>
          </card>
        </div>

        <BottomPagerBar v-model="pageNumber" item-name="voicemail" :per-page="pageSize"
          :total-rows="filteredVoicemails.length" :loading="loading" show-total show-page-size-selector
          @change-page-size="pageSize = $event">
          <template #left-after>
            <b-dropdown
              :text="transcriptCompactMode ? 'Compact Transcript View' : 'Expanded Transcript View'"
              variant="primary"
              size="sm"
              dropup>
              <b-dropdown-item
                :active="!transcriptCompactMode"
                @click="setTranscriptViewMode(false)">
                <i class="fas fa-list mr-2"></i>
                Expanded View (with timestamps)
              </b-dropdown-item>
              <b-dropdown-item
                :active="transcriptCompactMode"
                @click="setTranscriptViewMode(true)">
                <i class="fas fa-align-left mr-2"></i>
                Compact View (text only)
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </BottomPagerBar>
      </card>
    </b-col>
  </b-row>
</template>
<script lang="ts">
import Component from "vue-class-component";
import SearchComponent from "@/components/SearchComponent.vue";
import Axios, { AxiosResponse } from "axios";
import Vue from "vue";
import { Voicemail, VoicemailGroupCount } from "@/models/Voicemail";
import { Team } from "@/models/Team";
import { Watch } from "vue-property-decorator";
import moment from "moment";
import { Patient } from "@/models/Patient";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import DateRangeFilter from "@/components/DateRangeFilter.vue";
import TranscriptionViewer from "@/components/TranscriptionViewer.vue";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import vSelect from "vue-select";

@Component({
  components: {
    BottomPagerBar,
    SearchComponent,
    DateRangeFilter,
    TranscriptionViewer,
    vSelect,
  },
})
export default class VoicemailsPage extends Vue {
  private patients: { [key: number]: Patient } = {};
  private selectedTeam: number | string | null = null;  // 'all' = all voicemails, null = untriaged, number = specific team
  private triageLocation: number | null = null;
  private allTeams: Team[] = [];  // All teams for triage dropdown
  private incompleteCounts: VoicemailGroupCount[] = [];  // Teams with voicemails for filter dropdown
  private voicemails: Voicemail[] = [];
  private pharmacist: string = '';
  private selectedView: string = 'Incomplete';
  private selectedVoicemail: number = 0;
  private loading = false;
  private transcriptCompactMode: boolean = false;

  // Audio playback tracking for transcript highlighting
  private currentPlaybackTime: number = 0;
  private activeVoicemailId: number | null = null;

  // Audio URL state tracking
  private audioUrls: { [key: number]: string } = {};
  private audioUrlExpiry: { [key: number]: number } = {};
  private loadingAudioUrls: { [key: number]: boolean } = {};
  private downloadErrors: { [key: number]: string } = {};

  private createdDateRangeFilter: any = { startDate: moment().subtract(90, "days").toDate(), endDate: new Date() };
  private counts = { incomplete: 0, complete: 0 };

  protected pageNumber: number = 1;
  protected pageSize: number = 25;

  get routeVoicemailId(): number {
    const res = parseInt(this.$route.params.id);
    if (isNaN(res)) return 0;
    return res;
  }

  mounted() {
    // Load transcript view preference from localStorage
    const savedMode = localStorage.getItem('transcriptViewMode');
    this.transcriptCompactMode = savedMode === 'compact';

    this.getOnCallPharmacist();
    this.getCounts();
    this.getTeams();
    this.getIncompleteCountsByTeam();

    if (this.routeVoicemailId) {
      this.getSingleVoicemail();
      return;
    }

    this.getVoicemails();
  }

  relativeTime(item: Voicemail): string {
    const voicemailReceivedTime = moment.utc(item.received);
    return `${voicemailReceivedTime.fromNow()} (${voicemailReceivedTime.local().format("L LT")})`;
  }

  setTranscriptViewMode(compact: boolean) {
    this.transcriptCompactMode = compact;
    localStorage.setItem('transcriptViewMode', compact ? 'compact' : 'expanded');
  }

  expandIfClosed(item: Voicemail) {
    const idToExpand = `accordion-${item.id}`;
    const isCurrentlyExpanded = (this.$refs[idToExpand] as any)?.[0]?.show;

    // Only expand if currently closed
    if (!isCurrentlyExpanded) {
      this.toggleInfo(item);
    }
  }

  toggleInfo(item: Voicemail) {
    this.selectedVoicemail = item.id;
    // console.log("toggle info", item);

    // Initialize patient for this voicemail if it doesn't exist
    if (!this.patients[item.id]) {
      this.$set(this.patients, item.id, new Patient());
    }

    const idToExpand = `accordion-${item.id}`;
    const isCurrentlyExpanded = (this.$refs[idToExpand] as any)?.[0]?.show;

    // Pause currently playing audio when expanding a different item
    if (!isCurrentlyExpanded && this.activeVoicemailId && this.activeVoicemailId !== item.id) {
      const currentAudioElement = document.getElementById(`audio-${this.activeVoicemailId}`) as HTMLAudioElement;
      if (currentAudioElement && !currentAudioElement.paused) {
        currentAudioElement.pause();
      }
    }

    (this.$refs[idToExpand] as any)[0].show = false;

    this.$root.$emit("bv::toggle::collapse", idToExpand);

    // Load audio URL when expanding
    if (!isCurrentlyExpanded) {
      this.$nextTick(() => {
        this.loadAudioUrl(item.id);
      });
    }
  }

  autoselect(objects: Patient[]) {
    if (objects.length > 1) {
      // let searchComponent: any = document.getElementById(`audio-${itemId}`);
      return undefined;
    }
    if (objects.length > 0) return objects[0];
    return undefined;
  }

  get pagedVoicemails() {
    const startIndex = this.pageSize * this.pageNumber - this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.filteredVoicemails.slice(startIndex, endIndex);
  }

  get filteredVoicemails() {
    if (this.loading) return [];

    if (this.selectedView === "Incomplete" || !this.createdDateRangeFilter.startDate) {
      return this.voicemails;
    }

    return this.voicemails.filter(i => {
      let received = i.received;
      if (!received) return false;
      received = moment(i.received).toDate();

      return (received >= moment(this.createdDateRangeFilter.startDate).toDate()) &&
        (received < moment(this.createdDateRangeFilter.endDate).add(1, "day").toDate());
    })
  }

  async getSingleVoicemail() {
    try {
      const response = await Axios.get<Voicemail>(`/Voicemail/${this.routeVoicemailId}`);
      const voicemail = Object.assign(new Voicemail(), response.data);
      this.voicemails = [voicemail];

      // Initialize empty Patient object for the voicemail to ensure SearchComponent works correctly
      if (!this.patients[voicemail.id]) {
        this.$set(this.patients, voicemail.id, new Patient());
      }

      // console.log(this.voicemails);
      this.$nextTick(() => {
        this.toggleInfo(voicemail);
      });
    } catch (error) {
      console.error("Problem loading voicemail information by route Id", {
        error,
        response: (error as any)?.response
      });
      this.$notification(NotificationOptions.error(error));
    }
  }

  @Watch("selectedTeam")
  @Watch("selectedView")
  async getVoicemails() {
    try {
      this.loading = true;

      let response: AxiosResponse<Voicemail[]>;

      if (this.selectedView === "Incomplete") {
        // 'all' = all incomplete voicemails, null = untriaged, number = specific team
        if (this.selectedTeam === 'all') {
          response = await Axios.get<Voicemail[]>(`/Voicemail/AllIncomplete`);
        } else if (this.selectedTeam === null) {
          response = await Axios.get<Voicemail[]>(`/Voicemail/Untriaged`);
        } else {
          response = await Axios.get<Voicemail[]>(`/Voicemail/Incomplete/${this.selectedTeam}`);
        }
      } else {
        response = await Axios.get<Voicemail[]>(`/Voicemail/Completed`);
      }

      this.voicemails = response.data?.map(value => new Voicemail(value));

      // Initialize empty Patient objects for each voicemail to ensure SearchComponent works correctly
      this.voicemails.forEach(voicemail => {
        if (!this.patients[voicemail.id]) {
          this.$set(this.patients, voicemail.id, new Patient());
        }
      });
    } catch (error) {
      let errorMessage = "Error Fetching Voicemails";
      const err = error as any;
      if (err && err?.response?.data) {
        errorMessage += ` - ${err.response.data}`;
      }
      this.$notification(NotificationOptions.notificationOptionsPreset(errorMessage, NotificationOptions.NotificationTypes.danger));
    } finally {
      this.loading = false;
    }
  }

  async triageVoicemail(item: Voicemail) {
    if (this.triageLocation === null) {
      this.$notification(NotificationOptions.error("Please select a team to triage to"));
      return;
    }

    try {
      await Axios.post(`/Voicemail/Triage/${item.id}/${this.triageLocation}`);
      await this.getVoicemails();
      await this.getIncompleteCountsByTeam();  // Refresh the filter dropdown
    } catch (error) {
      this.$notification(NotificationOptions.errorSaveNotificationPreset("Voicemail Triage", error as any));
    }
  }

  goToPatient(patient: Patient) {
    this.$router.push(`/Patient/${patient.id}`);
  }

  async completeVoicemail(item: Voicemail) {
    const patientId = Number(this.patients[item.id]?.id);
    if (!Number.isNaN(patientId)) {
      item.patientId = patientId;
    }

    try {
      await Axios.post(`Voicemail/Complete`, item);
      this.$delete(this.patients, item.id);
      await this.getVoicemails();
    } catch (error) {
      this.$notification(NotificationOptions.errorSaveNotificationPreset("Voicemail Complete", error as any));
    }
  }

  async loadAudioUrl(itemId: number, forceRefresh: boolean = false) {
    // Prevent duplicate requests
    if (this.loadingAudioUrls[itemId]) {
      return;
    }

    // Check if URL is expired (use 25 min as safety buffer for 30 min expiry)
    const isExpired = this.audioUrlExpiry[itemId] && Date.now() > this.audioUrlExpiry[itemId];

    // Already loaded - reuse URL if not expired
    if (this.audioUrls[itemId] && !isExpired && !forceRefresh) {
      let audioItem: any = document.getElementById(`audio-${itemId}`);
      let sourceItem: any = document.getElementById(`source-${itemId}`);

      if (audioItem && sourceItem) {
        sourceItem.src = this.audioUrls[itemId];

        // Reattach event listeners (may have been lost)
        this.attachVoicemailAudioEventListeners(audioItem, itemId);

        audioItem.hidden = false;
        audioItem.load();
      }
      return;
    }

    // Clear expired or force-refreshed URL
    if (isExpired || forceRefresh) {
      delete this.audioUrls[itemId];
      delete this.audioUrlExpiry[itemId];
    }

    this.loadingAudioUrls[itemId] = true;
    delete this.downloadErrors[itemId]; // Clear any previous errors

    try {
      let response = await Axios.get(`/Voicemail/GetAudioUrl/${itemId}`);
      const url = response.data.url;

      // Store presigned URL and expiry time (25 minutes = 1500000 ms)
      this.audioUrls[itemId] = url;
      this.audioUrlExpiry[itemId] = Date.now() + 1500000;

      let sourceItem: any = document.getElementById(`source-${itemId}`);
      sourceItem.src = url;
      let audioItem: any = document.getElementById(`audio-${itemId}`);

      // Add event listeners for transcript highlighting
      this.attachVoicemailAudioEventListeners(audioItem, itemId);

      audioItem.hidden = false;
      audioItem.pause();
      audioItem.load();
    } catch (err: any) {
      this.$set(this.downloadErrors, itemId, 'Failed to load audio');
    } finally {
      this.$set(this.loadingAudioUrls, itemId, false);
    }

  }

  private attachVoicemailAudioEventListeners(audioItem: any, itemId: number) {
    audioItem.addEventListener('timeupdate', () => {
      if (this.activeVoicemailId === itemId) {
        this.currentPlaybackTime = audioItem.currentTime;
      }
    });

    audioItem.addEventListener('play', () => {
      this.activeVoicemailId = itemId;
      this.currentPlaybackTime = audioItem.currentTime;
    });

    audioItem.addEventListener('pause', () => {
      if (this.activeVoicemailId === itemId) {
        this.activeVoicemailId = null;
        this.currentPlaybackTime = 0;
      }
    });

    audioItem.addEventListener('ended', () => {
      if (this.activeVoicemailId === itemId) {
        this.activeVoicemailId = null;
        this.currentPlaybackTime = 0;
      }
    });

    // Handle audio load errors (e.g., expired presigned URL)
    audioItem.addEventListener('error', async () => {
      // Only auto-retry once to avoid infinite loops
      if (!this.downloadErrors[itemId]) {
        console.log(`Audio load error for ${itemId}, refreshing URL...`);
        await this.loadAudioUrl(itemId, true);
      }
    });
  }

  transcriptClicked(voicemailId: number, startTime: any) {
    let timespan = moment.duration(startTime);
    let audioItem: any = document.getElementById(`audio-${voicemailId}`);
    if (audioItem.getAttribute('hidden')) {
      this.loadAudioUrl(voicemailId);
    }
    audioItem.currentTime = timespan.asSeconds();

    // If audio is not currently playing, start playing it
    if (audioItem.paused) {
      audioItem.play();
    }
  }

  async getOnCallPharmacist() {
    const response = await Axios.get(`/Voicemail/OnCallPharmacist`);
    this.pharmacist = response.data;
  }

  async getCounts() {
    const response = await Axios.get("/Voicemail/IncompleteCompleteCounts");
    this.counts = response.data;
  }

  async getTeams() {
    try {
      const response = await Axios.get<Team[]>("/User/teams");
      this.allTeams = response.data;
    } catch (error) {
      console.error("Error fetching teams", error);
    }
  }

  async getIncompleteCountsByTeam() {
    try {
      const response = await Axios.get<VoicemailGroupCount[]>("/Voicemail/IncompleteCountsByTeam");
      this.incompleteCounts = response.data;

      // If the currently selected team no longer has any voicemails, reset to 'all'
      if (this.selectedTeam !== 'all' && this.selectedTeam !== null) {
        const teamStillExists = this.incompleteCounts.some(c => c.triagedTo === this.selectedTeam);
        if (!teamStillExists) {
          this.selectedTeam = 'all';
        }
      }
    } catch (error) {
      console.error("Error fetching incomplete counts by team", error);
    }
  }

  // Filter dropdown: All option + Untriaged + teams that have voicemails
  get filterTeamOptions() {
    const teamsWithVoicemails = this.incompleteCounts
      .filter(c => c.triagedTo !== null)
      .map(c => ({ id: c.triagedTo, name: c.teamName || 'Unknown Team' }));
    return [{ id: 'all', name: "All" }, { id: null, name: "Untriaged" }, ...teamsWithVoicemails];
  }

  get selectViewOptions() {
    return [
      { text: this.counts.incomplete ? `Incomplete (${this.counts.incomplete})` : "Incomplete", value: 'Incomplete' },
      { text: this.counts.complete ? `Complete (${this.counts.complete})` : "Complete", value: 'Complete' }
    ];
  }

  getCurrentTimeForVoicemail(voicemailId: number): number | null {
    return this.activeVoicemailId === voicemailId ? this.currentPlaybackTime : null;
  }
}
</script>

<style scoped>
/* Active dropdown item styling */
::v-deep .dropdown-menu .dropdown-item.active,
::v-deep .dropdown-menu .dropdown-item.active:hover,
::v-deep .dropdown-menu .dropdown-item.active:focus {
  background-color: #007bff !important;
  color: white !important;
  font-weight: bold !important;
}

::v-deep .dropdown-menu .dropdown-item.active i,
::v-deep .dropdown-menu .dropdown-item.active:hover i,
::v-deep .dropdown-menu .dropdown-item.active:focus i {
  color: white !important;
}

::v-deep .dropdown-menu .dropdown-item.active *,
::v-deep .dropdown-menu .dropdown-item.active:hover *,
::v-deep .dropdown-menu .dropdown-item.active:focus * {
  color: white !important;
}
</style>
